/**
 * **Which raves this phone keeps for offline — by their dates** (the user, 2026-10-30: "prioritise them by dates,
 * upcoming raves come first, and when one is over +2 days remove it from the offline and download the new one").
 *
 * The list: the raves this account attends whose end is less than `ENDED_KEEP_MS` ago, one per rave address (all
 * of a rave's editions share it), soonest start first. The iPhone app keeps the first `IOS_SLOTS` — WebKit lets an
 * app on app-bound domains hold three service workers (`SWServer.cpp` `defaultMaxRegistrationCount`); a browser and
 * the Android app keep them all. A rave off the list gives its offline copy back (`RaveOffline`).
 *
 * **Freeing a slot** (the iPhone app only): a page can unregister only its OWN address's worker, so when a rave on
 * the list is refused, the page hops through each attended rave that is OFF the list — `?myrave-release=` on that
 * rave's own address unregisters there and moves on (`releaseHop`, run before anything renders, `main.tsx`) — and
 * comes back to the rave that asked, which then keeps itself. At most once per `RELEASE_EVERY_MS` per rave, and only
 * ever to the platform's own rave addresses.
 */
import { readNativeApp } from '@myrave/shared';
import { buildFestivalUrl, platformDomain, resolveTenant } from '@/lib/tenant';
import { readJson, writeJson } from '@/lib/storage';
import { dropWorker } from './register';

export const ENDED_KEEP_MS = 2 * 24 * 60 * 60_000;
export const IOS_SLOTS = 3;
export const RELEASE_EVERY_MS = 6 * 60 * 60_000;
const RELEASE_PARAM = 'myrave-release';

export interface SlotRave { subdomain: string; gatesOpenAt: string | null; endsAt: string | null; attending?: boolean }

/** The rave addresses kept for offline, in order — `limit` of them. */
export function raveSlots(raves: readonly SlotRave[], now: number, limit = Infinity): string[] {
  const start = new Map<string, number>();
  for (const r of raves) {
    if (r.attending === false) continue;
    const end = r.endsAt ? Date.parse(r.endsAt) : NaN;
    if (Number.isFinite(end) && end + ENDED_KEEP_MS <= now) continue;
    const from = r.gatesOpenAt ? Date.parse(r.gatesOpenAt) : Infinity;
    start.set(r.subdomain, Math.min(start.get(r.subdomain) ?? Infinity, Number.isFinite(from) ? from : Infinity));
  }
  return [...start.entries()].sort((a, b) => a[1] - b[1] || (a[0] < b[0] ? -1 : 1)).map(([s]) => s).slice(0, limit);
}

export function slotLimit(userAgent: string): number {
  return readNativeApp(userAgent)?.os === 'ios' ? IOS_SLOTS : Infinity;
}

/**
 * What this rave's address does: `keep` it for offline, or `drop` its copy. Off the iPhone, a rave you do not
 * attend is kept as before (a browser has no three); one you attend that ended over two days ago is dropped.
 */
export function slotDecision(subdomain: string, raves: readonly SlotRave[], now: number, userAgent: string): 'keep' | 'drop' {
  const limit = slotLimit(userAgent);
  if (raveSlots(raves, now, limit).includes(subdomain)) return 'keep';
  if (limit !== Infinity) return 'drop';
  const attended = raves.some((r) => r.subdomain === subdomain && r.attending !== false);
  return attended ? 'drop' : 'keep';
}

/** The attended rave addresses that may hold a slot they should not: off the list, never this one. */
export function releaseList(subdomain: string, raves: readonly SlotRave[], now: number, userAgent: string): string[] {
  const keep = new Set(raveSlots(raves, now, slotLimit(userAgent)));
  const all = new Set(raves.filter((r) => r.attending !== false).map((r) => r.subdomain));
  return [...all].filter((s) => s !== subdomain && !keep.has(s)).sort();
}

const raveUrl = (subdomain: string) => buildFestivalUrl(subdomain, platformDomain(), window.location.protocol, window.location.port);

/** The first hop of a release that ends back at `back`, or null when there is nothing to free or it ran lately. */
export function startRelease(list: readonly string[], back: string, now = Date.now()): string | null {
  if (list.length === 0) return null;
  const last = readJson<number | null>(null, 'sw-release-at', null);
  if (last !== null && now - last < RELEASE_EVERY_MS) return null;
  writeJson(null, 'sw-release-at', now);
  return hopUrl(list, back);
}

/**
 * Give back the offline copies of `list`'s rave addresses, then land on `back` — at once, no six-hour wait: the raver
 * asked for it (Raves on the same dates, the user, 2026-11-02: "switch the active rave … and clear the other raves
 * from offline from the device"). `back` itself when there is nothing to free.
 */
export function releaseThen(list: readonly string[], back: string): string {
  return list.length ? hopUrl(list, back) : back;
}

function hopUrl(list: readonly string[], back: string): string {
  const [first, ...rest] = list;
  const u = new URL(`${raveUrl(first!)}/`);
  u.searchParams.set(RELEASE_PARAM, rest.join(','));
  u.searchParams.set('next', back);
  return u.toString();
}

/** Only the platform's own addresses, over https (or this page's own scheme on dev): never an open redirect. */
function onPlatform(url: string): boolean {
  try {
    const u = new URL(url);
    const d = platformDomain().toLowerCase();
    const h = u.hostname.toLowerCase();
    return (u.protocol === 'https:' || u.protocol === window.location.protocol) && (h === d || h.endsWith(`.${d}`));
  } catch { return false; }
}

/** This page is a release hop: what to free here and where to go next — or null for an ordinary page. */
export function releaseHop(href: string): { rest: string[]; next: string } | null {
  let u: URL;
  try { u = new URL(href); } catch { return null; }
  if (!u.searchParams.has(RELEASE_PARAM)) return null;
  const next = u.searchParams.get('next') ?? '';
  if (!onPlatform(next) || resolveTenant(u.hostname).subdomain === null) return null;
  const rest = (u.searchParams.get(RELEASE_PARAM) ?? '').split(',').map((s) => s.trim().toLowerCase())
    .filter((s) => /^[a-z0-9-]{1,63}$/.test(s));
  return { rest, next };
}

/** Free this address's slot, then the next rave's — and home to the rave that asked. */
export async function runReleaseHop(hop: { rest: string[]; next: string }): Promise<void> {
  if ('serviceWorker' in navigator) await dropWorker();
  window.location.replace(hop.rest.length ? hopUrl(hop.rest, hop.next) : hop.next);
}
